/* eslint-disable max-lines */
import type { Side } from "@design-lab/shared";
import {
  trackArtworkLongProcessing,
  trackDesignLabImageProcessingCompleted,
  trackImageEnhancementCompleted,
  trackImageEnhancementStarted
} from "@design-lab/shared/lib/analytics";
import type { models } from "jiffy-api";
import type { DistributedOmit } from "type-fest";
import { assertEvent, assign, fromCallback, sendTo, setup } from "xstate";
import type { Artwork } from "@design-lab/shared/types/artwork";
import { enhancementActor, type EnhancementActorInput } from "./enhancement.actor";

export type EnhancementSource = "upload" | "design_library" | "canva" | "ai_generation" | "camera";

export type ArtworkMachineState =
  | "initial"
  | "progress"
  | "uploaded"
  | "enhancing"
  | "warming"
  | "finished";

export type ArtworkMachineContext = {
  side: Side;
  file?: File | undefined;
  artworkId?: number | undefined;
  processTimeStamp?: string | undefined;
  originalImageUrl?: Artwork["originalImageUrl"] | undefined;
  artwork?: Artwork | undefined;
  startTime?: number | undefined;
  source?: EnhancementSource | undefined;
  lastWebsocketEvent?: string | undefined;
  cacheWarmingStatus: "started" | "warmed" | "none";
  visitedStates: ArtworkMachineState[];
  shouldRedirect?: boolean | undefined;
  completionTriggerEvent?: string | undefined;
  /**
   * If true, UI effects like side auto-switch should be suppressed.
   * Set by copy flow and cleared automatically on finish.
   */
  suppressAutoSwitch?: boolean | undefined;
};

export const artworkMachine = setup({
  types: {
    context: {} as ArtworkMachineContext,
    tags: {} as "loading",
    events: {} as
      | {
          type: "reset";
          context?: DistributedOmit<ArtworkMachineContext, "side">;
          targetState?: "initial" | "finished";
        }
      | { type: "initial" }
      | { type: "upload.started"; file: File; source: EnhancementSource; processTimeStamp: string }
      | { type: "upload.completed"; id: number }
      | { type: "set.originalImageUrl"; originalImageUrl: Artwork["originalImageUrl"] }
      | { type: "enhancement.started" }
      | { type: "enhancement.completed"; artwork: Artwork; triggerEvent?: string }
      | { type: "cache.warming.started" }
      | { type: "cache.warmed" }
      | { type: "finished" }
      | { type: "websocket.event"; name: models.cable.DtfChannel.Actions["action"] }
      | { type: "progress.tick" },
    input: {} as { side: Side }
  },
  actors: {
    progressTicker: fromCallback(({ sendBack }) => {
      const intervalId = setInterval(() => {
        sendBack({ type: "progress.tick" });
      }, 60_000);
      return () => clearInterval(intervalId);
    }),
    enhancement: enhancementActor
  },
  actions: {
    selectDesignTab: () => {
      // This action will be passed to the machine
    },
    highlightJiffyDesignService: () => {
      // This action will be passed to the machine
    },
    resetContext: assign(({ event, context }) => {
      assertEvent(event, "reset");

      const emptyContext: ArtworkMachineContext = {
        file: undefined,
        artworkId: undefined,
        processTimeStamp: undefined,
        artwork: undefined,
        originalImageUrl: undefined,
        startTime: undefined,
        source: undefined,
        lastWebsocketEvent: undefined,
        cacheWarmingStatus: "none",
        visitedStates: [],
        side: context.side,
        shouldRedirect: true,
        completionTriggerEvent: undefined,
        suppressAutoSwitch: false
      };

      return {
        ...emptyContext,
        ...event.context,
        visitedStates: event.context?.visitedStates ?? []
      };
    }),
    trackEnhancementStarted: ({ context }) => {
      if (context.artworkId !== undefined) {
        trackImageEnhancementStarted(
          String(context.artworkId),
          "process_dtf_artwork_started",
          context.source
        );
      }
    },
    trackEnhancementCompleted: ({ context }) => {
      if (context.artworkId !== undefined) {
        trackImageEnhancementCompleted({
          artworkId: String(context.artworkId),
          enhancementType: "process_artwork",
          startTime: context.startTime || 0,
          ...(context.source ? { source: context.source } : {}),
          ...(context.completionTriggerEvent
            ? { triggerEvent: context.completionTriggerEvent }
            : {})
        });

        if (context.artwork) {
          trackDesignLabImageProcessingCompleted(context.artwork, String(context.artworkId));
        }
      }
    },
    logProgressTick: ({ context }) => {
      const { artworkId, startTime, source, lastWebsocketEvent, visitedStates } = context;
      const durationMs = Math.max(0, Date.now() - (startTime || 0));

      trackArtworkLongProcessing({
        side: context.side,
        artworkId: String(artworkId),
        startTime: startTime || 0,
        ...(source ? { source } : {}),
        durationMs,
        lastWebsocketEvent: lastWebsocketEvent || "none",
        passedSteps: visitedStates,
        cacheWarmingStatus: context.cacheWarmingStatus
      });
    },
    acknowledgeArtwork: () => {
      // Implementation passed from useArtwork
    },
    invalidateArtworkQueries: () => {
      // Implementation passed from useArtwork
    }
  }
}).createMachine({
  context: ({ input }) => ({
    visitedStates: [],
    side: input.side,
    cacheWarmingStatus: "none",
    shouldRedirect: true,
    processTimeStamp: undefined,
    suppressAutoSwitch: false
  }),
  id: "artwork",
  initial: "initial",
  on: {
    reset: [
      {
        target: ".finished",
        guard: ({ event }) => event.targetState === "finished",
        actions: "resetContext"
      },
      {
        target: ".initial",
        actions: "resetContext"
      }
    ],
    "websocket.event": {
      actions: assign(({ event }) => {
        assertEvent(event, "websocket.event");
        return { lastWebsocketEvent: event.name };
      })
    },
    "progress.tick": { actions: "logProgressTick" },
    "set.originalImageUrl": {
      actions: assign(({ event }) => {
        assertEvent(event, "set.originalImageUrl");
        return { originalImageUrl: event.originalImageUrl };
      })
    }
  },
  states: {
    initial: {
      entry: assign(({ context }) => ({
        visitedStates: [...context.visitedStates, "initial"]
      })),
      on: {
        "upload.started": {
          target: "processing.progress",
          actions: assign({
            startTime: () => Date.now(),
            source: ({ event }) => event.source,
            file: ({ event }) => event.file,
            processTimeStamp: ({ event }) => event.processTimeStamp
          })
        },
        "enhancement.started": {
          target: "processing.enhancing"
        }
      }
    },
    processing: {
      initial: "progress",
      invoke: [
        { src: "progressTicker" },
        {
          id: "enhancement",
          src: "enhancement",
          input: ({ context }) => {
            const input: EnhancementActorInput = {};
            if (context.file !== undefined) input.file = context.file;
            if (context.artworkId !== undefined) input.artworkId = context.artworkId;
            if (context.source !== undefined) input.source = context.source;
            return input;
          }
        }
      ],
      states: {
        progress: {
          entry: [
            assign(({ context }) => ({
              visitedStates: [...context.visitedStates, "progress"]
            }))
          ],
          on: {
            "upload.completed": {
              target: "uploaded",
              actions: [
                assign({ artworkId: ({ event }) => event.id }),
                sendTo("enhancement", ({ event }) => event)
              ]
            }
          },
          tags: "loading"
        },
        uploaded: {
          entry: assign(({ context }) => ({
            visitedStates: [...context.visitedStates, "uploaded"]
          })),
          on: {
            "enhancement.started": {
              target: "enhancing",
              actions: ["trackEnhancementStarted", sendTo("enhancement", ({ event }) => event)]
            },
            "enhancement.completed": {
              actions: [
                assign(({ event }) => {
                  assertEvent(event, "enhancement.completed");
                  return { artworkId: event.artwork?.id };
                }),
                sendTo("enhancement", ({ event }) => event)
              ]
            }
          },
          tags: "loading"
        },
        enhancing: {
          entry: assign(({ context }) => ({
            visitedStates: [...context.visitedStates, "enhancing"]
          })),
          on: {
            "cache.warming.started": {
              target: "warming",
              actions: [
                assign({ cacheWarmingStatus: "started" }),
                sendTo("enhancement", ({ event }) => event)
              ]
            },
            "enhancement.completed": {
              target: "#artwork.finished",
              actions: [
                assign(({ event }) => {
                  assertEvent(event, "enhancement.completed");
                  return {
                    artwork: event.artwork,
                    artworkId: event.artwork?.id,
                    file: undefined,
                    completionTriggerEvent: event.triggerEvent
                  };
                }),
                "trackEnhancementCompleted",
                "acknowledgeArtwork",
                "invalidateArtworkQueries"
              ]
            }
          },
          tags: "loading"
        },
        warming: {
          entry: assign(({ context }) => ({
            visitedStates: [...context.visitedStates, "warming"]
          })),
          on: {
            "cache.warmed": {
              actions: [
                assign({ cacheWarmingStatus: "warmed" }),
                sendTo("enhancement", ({ event }) => event)
              ]
            },
            "enhancement.completed": {
              target: "#artwork.finished",
              actions: [
                assign(({ event }) => {
                  assertEvent(event, "enhancement.completed");
                  return {
                    artwork: event.artwork,
                    artworkId: event.artwork?.id,
                    file: undefined,
                    completionTriggerEvent: event.triggerEvent
                  };
                }),
                "trackEnhancementCompleted",
                "acknowledgeArtwork",
                "invalidateArtworkQueries"
              ]
            }
          },
          tags: "loading"
        }
      }
    },
    finished: {
      entry: assign(({ context }) => ({
        visitedStates: [...context.visitedStates, "finished"],
        // Clear suppression once processing completes
        suppressAutoSwitch: false
      }))
    }
  }
});
